import React, { useMemo, useState } from 'react';
import { Card, CardBody } from 'reactstrap';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';

import QueueItem from './components/QueueItem';
import ActiveTicketItem from './components/ActiveTicketItem';
import RecentTicketItem from './components/RecentTicketItem';

const sectionTitleStyle = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#5F5E5A',
  padding: '8px 4px 6px',
};

const pulseStyle = document.createElement('style');
pulseStyle.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;
if (!document.querySelector('[data-viddy-pulse]')) {
  pulseStyle.setAttribute('data-viddy-pulse', 'true');
  document.head.appendChild(pulseStyle);
}

export default function QueuePanel({
  queue,
  myTickets,
  activeTicket,
  recentTickets,
  onPickUp,
  onSelectTicket,
  onSelectRecent,
  onChangePeriod,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [recentPeriod, setRecentPeriod] = useState('48');

  const filteredQueue = useMemo(() => {
    if (!searchTerm.trim()) return queue;
    const term = searchTerm.toLowerCase();
    return queue.filter(
      item =>
        (item.customer_name || '').toLowerCase().includes(term) ||
        (item.order_number || '').toLowerCase().includes(term) ||
        (item.store_name || '').toLowerCase().includes(term) ||
        (item.subscriber_name || '').toLowerCase().includes(term)
    );
  }, [queue, searchTerm]);

  const filteredTickets = useMemo(() => {
    if (!searchTerm.trim()) return myTickets;
    const term = searchTerm.toLowerCase();
    return myTickets.filter(
      ticket =>
        (ticket.customer_name || '').toLowerCase().includes(term) ||
        (ticket.order_number || '').toLowerCase().includes(term) ||
        (ticket.store_name || '').toLowerCase().includes(term) ||
        (ticket.subscriber_name || '').toLowerCase().includes(term)
    );
  }, [myTickets, searchTerm]);

  const filteredRecent = useMemo(() => {
    if (!searchTerm.trim()) return recentTickets;
    const term = searchTerm.toLowerCase();
    return recentTickets.filter(
      ticket =>
        (ticket.customer_name || '').toLowerCase().includes(term) ||
        (ticket.order_number || '').toLowerCase().includes(term) ||
        (ticket.store_name || '').toLowerCase().includes(term)
    );
  }, [recentTickets, searchTerm]);

  const waitingCount = useMemo(() => queue.length, [queue]);
  const activeCount = useMemo(() => myTickets.length, [myTickets]);
  const recentCount = useMemo(() => recentTickets.length, [recentTickets]);

  return (
    <Card
      className="card new-layout mb-0 d-flex flex-column"
      style={{
        border: '0.5px solid #dee2e6',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 14px',
          borderBottom: '0.5px solid #dee2e6',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#3d3d3a',
            marginBottom: '6px',
          }}
        >
          Atendimentos
        </div>
        <div className="d-flex" style={{ gap: '6px' }}>
          {waitingCount > 0 && (
            <span
              style={{
                background: '#FCEBEB',
                color: '#A32D2D',
                fontSize: '11px',
                padding: '2px 8px',
                borderRadius: '10px',
                animation: 'pulse 2s ease-in-out 3',
              }}
            >
              {waitingCount} aguardando
            </span>
          )}
          {activeCount > 0 && (
            <span
              style={{
                background: '#E6F1FB',
                color: '#0C447C',
                fontSize: '11px',
                padding: '2px 8px',
                borderRadius: '10px',
              }}
            >
              {activeCount} {activeCount === 1 ? 'ativo' : 'ativos'}
            </span>
          )}
          {recentCount > 0 && (
            <span
              style={{
                background: '#f2f2f0',
                color: '#888780',
                fontSize: '11px',
                padding: '2px 8px',
                borderRadius: '10px',
              }}
            >
              {recentCount} {recentCount === 1 ? 'encerrado' : 'encerrados'}
            </span>
          )}
          {waitingCount === 0 && activeCount === 0 && (
            <span style={{ fontSize: '11px', color: '#888780' }}>
              Nenhum atendimento
            </span>
          )}
        </div>
        <input
          type="text"
          placeholder="Buscar por nome, pedido..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 10px',
            marginTop: '10px',
            fontSize: '12px',
            border: '0.5px solid #dee2e6',
            borderRadius: '6px',
            outline: 'none',
            background: '#f8f8f6',
          }}
        />
      </div>

      {/* Lista scrollável */}
      <CardBody
        className="p-0"
        style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
      >
        <PerfectScrollbar style={{ height: '100%', padding: '6px 8px' }}>
          {/* Fila de espera */}
          {filteredQueue.length > 0 && (
            <>
              <div style={sectionTitleStyle}>Fila de espera</div>
              {filteredQueue.map(item => (
                <QueueItem key={item.id} item={item} onPickUp={onPickUp} />
              ))}
            </>
          )}

          {/* Meus atendimentos */}
          {filteredTickets.length > 0 && (
            <>
              <div
                style={{
                  ...sectionTitleStyle,
                  borderTop:
                    filteredQueue.length > 0 ? '0.5px solid #dee2e6' : 'none',
                  marginTop: filteredQueue.length > 0 ? '6px' : '0',
                  paddingTop: filteredQueue.length > 0 ? '10px' : '8px',
                }}
              >
                Meus atendimentos
              </div>
              {filteredTickets.map(ticket => (
                <ActiveTicketItem
                  key={ticket.id}
                  ticket={ticket}
                  isActive={activeTicket?.id === ticket.id}
                  onSelect={onSelectTicket}
                />
              ))}
            </>
          )}

          {/* Recentes */}
          {(filteredRecent.length > 0 || recentTickets.length > 0) && (
            <>
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  ...sectionTitleStyle,
                  borderTop:
                    filteredQueue.length > 0 || filteredTickets.length > 0
                      ? '0.5px solid #dee2e6'
                      : 'none',
                  marginTop:
                    filteredQueue.length > 0 || filteredTickets.length > 0
                      ? '6px'
                      : '0',
                  paddingTop:
                    filteredQueue.length > 0 || filteredTickets.length > 0
                      ? '10px'
                      : '8px',
                  color: '#888780',
                }}
              >
                <span>Recentes</span>
                <select
                  value={recentPeriod}
                  onChange={e => {
                    setRecentPeriod(e.target.value);
                    if (onChangePeriod) onChangePeriod(e.target.value);
                  }}
                  style={{
                    fontSize: '10px',
                    border: '0.5px solid #dee2e6',
                    borderRadius: '4px',
                    padding: '2px 4px',
                    color: '#888780',
                    background: '#fff',
                    outline: 'none',
                  }}
                >
                  <option value="24">24h</option>
                  <option value="48">48h</option>
                  <option value="168">7 dias</option>
                  <option value="720">30 dias</option>
                </select>
              </div>
              {filteredRecent.map(ticket => (
                <RecentTicketItem
                  key={ticket.id}
                  ticket={ticket}
                  isActive={activeTicket?.id === ticket.id}
                  onSelect={onSelectRecent}
                />
              ))}
            </>
          )}

          {/* Sem resultados */}
          {searchTerm.trim() &&
            filteredQueue.length === 0 &&
            filteredTickets.length === 0 &&
            filteredRecent.length === 0 && (
              <div
                style={{
                  padding: '20px',
                  textAlign: 'center',
                  fontSize: '12px',
                  color: '#888780',
                }}
              >
                Nenhum resultado para &ldquo;{searchTerm}&rdquo;
              </div>
            )}
        </PerfectScrollbar>
      </CardBody>
    </Card>
  );
}
